import React from 'react';
import LogoMark from '../layout/LogoMark';
import VersionBadge from '../layout/VersionBadge';
import { useTranslation } from '../../utils/i18n';

export default function AuthScreen({
  isRegister,
  usernameInput,
  passwordInput,
  mnemonic,
  copiedMnemonic,
  authError,
  showRegSuccessPopup,
  derivedKeys,
  serverVersion,
  serverConsensus,
  dropTargetInput,
  dropSenderInput,
  dropMessageInput,
  dropStatus,
  dropError,
  onSubmit,
  onUsernameChange,
  onPasswordChange,
  onMnemonicChange,
  onDropTargetChange,
  onDropSenderChange,
  onDropMessageChange,
  onSubmitGaiaDrop,
  onGenerateMnemonic,
  onCopyMnemonic,
  onToggleMode,
  onCloseSuccess
}) {
  const { language, changeLanguage, t } = useTranslation();

  return (
    <div className="auth-wrapper auth-product-shell">
      {/* LANGUAGE SWITCHER */}
      <div className="auth-lang-container" style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 10 }}>
        <div className="language-selector-row">
          {['de', 'en', 'ru', 'es', 'fr'].map(lang => (
            <button
              key={lang}
              type="button"
              className={`lang-btn ${language === lang ? 'active' : ''}`}
              onClick={() => changeLanguage(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <section className="auth-story-panel">
        <LogoMark />
        <VersionBadge version={serverVersion} consensus={serverConsensus} />
        <h1>{t('auth_headline')}</h1>
        <p>{t('auth_subline')}</p>
        <div className="auth-proof-grid">
          <div><strong>E2EE</strong><span>{t('auth_proof_e2ee_desc')}</span></div>
          <div><strong>GaiaData</strong><span>{t('auth_proof_gaiadata_desc')}</span></div>
          <div><strong>Hybrid</strong><span>{t('auth_proof_hybrid_desc')}</span></div>
        </div>
        <div className="powered-by">Powered by VisionGaiaTechnology</div>
      </section>

      <section className="auth-card glass-panel">
        <div className="auth-header">
          <LogoMark compact />
          <p>{t('auth_secure_mail')}</p>
        </div>

        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label>{t('auth_username')}</label>
            <input
              type="text"
              className="input-field"
              placeholder={t('auth_username_placeholder')}
              value={usernameInput}
              onChange={event => onUsernameChange(event.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div className="form-group">
            <label>{t('auth_password')}</label>
            <input
              type="password"
              className="input-field"
              placeholder={t('auth_password_placeholder')}
              value={passwordInput}
              onChange={event => onPasswordChange(event.target.value)}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('auth_mnemonic')}</label>
            <textarea
              className="input-field"
              placeholder={t('auth_mnemonic_placeholder')}
              value={mnemonic}
              onChange={event => onMnemonicChange(event.target.value)}
              autoComplete="off"
              required
            />
            {isRegister && !mnemonic && (
              <button type="button" className="btn-secondary compact-btn" onClick={onGenerateMnemonic}>
                {t('auth_generate_seed')}
              </button>
            )}
            {mnemonic && (
              <div className="mnemonic-display">
                {mnemonic}
                <button type="button" className="btn-action" onClick={onCopyMnemonic}>
                  {copiedMnemonic ? t('auth_copied') : t('auth_copy_seed')}
                </button>
              </div>
            )}
          </div>

          {authError && <p className="form-error">{authError}</p>}

          <button type="submit" className="btn-primary">
            {isRegister ? t('auth_btn_register') : t('auth_btn_login')}
          </button>
        </form>

        <button type="button" className="btn-secondary" style={{ marginTop: '10px' }} onClick={onToggleMode}>
          {isRegister ? t('auth_toggle_register') : t('auth_toggle_login')}
        </button>

        <form onSubmit={onSubmitGaiaDrop} className="gaia-drop-public">
          <div className="profile-section-title">GaiaDrop</div>
          <p className="auth-copy">Secure Drop fuer externe Text-Hinweise ohne GaiaCOM Account.</p>
          <div className="form-group">
            <label>Ziel-GaiaID</label>
            <input
              type="text"
              className="input-field"
              placeholder="name@gaiacom.de"
              value={dropTargetInput}
              onChange={event => onDropTargetChange(event.target.value)}
              autoComplete="off"
              required
            />
          </div>
          <div className="form-group">
            <label>Absender-Hinweis optional</label>
            <input
              type="text"
              className="input-field"
              placeholder="Name, Aktenzeichen oder anonym"
              value={dropSenderInput}
              onChange={event => onDropSenderChange(event.target.value)}
              autoComplete="off"
              maxLength={80}
            />
          </div>
          <div className="form-group">
            <label>Nachricht</label>
            <textarea
              className="input-field"
              placeholder="Vertraulichen Text eingeben..."
              value={dropMessageInput}
              onChange={event => onDropMessageChange(event.target.value)}
              maxLength={5000}
              required
            />
          </div>
          {dropError && <p className="form-error">{dropError}</p>}
          {dropStatus && <p className="form-success">{dropStatus}</p>}
          <button type="submit" className="btn-secondary">
            GaiaDrop senden
          </button>
        </form>
      </section>

      {showRegSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel success-card">
            <div className="popup-title">{t('auth_success_title')}</div>
            <div className="popup-text">
              {t('auth_success_text')}
            </div>
            <div className="crypto-value">{derivedKeys?.sign.public}</div>
            <button className="btn-primary" onClick={onCloseSuccess}>{t('auth_success_btn')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
